import { ADMIN_CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@shared/constants/index.ts';
import type { ApiErrorBody } from '@shared/types/index.ts';
import type { AdminCompanyDetail, AdminCompanyList, AdminCompanyNote, AdminCompanyUser } from '@shared/types/admin.ts';
import type {
  AdminFeedbackAnalytics,
  AdminFeedbackDetail,
  AdminFeedbackRow,
  AdminFeedbackSettings,
} from '@shared/types/index.ts';
import type { ServerPublicKeyCreation, ServerPublicKeyRequest } from '../lib/webauthn.ts';

/**
 * The operator CMS's own API client. Separate from ./client.ts on purpose:
 * the base path, the CSRF cookie and the failure handling all differ — an
 * admin 401 must send the visitor to /admin/login, never trip the tenant
 * session handlers — and the admin UI is English-only, so none of the
 * localization machinery applies.
 */

export const ADMIN_API_BASE = '/api/admin/v1';

export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.error?.message || `The admin API answered ${status}.`);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.fields = body?.error?.fields ?? {};
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** A read that never answers must not leave an admin screen loading forever. */
export class AdminReadTimeoutError extends Error {
  constructor() {
    super('The admin request took too long. Please try again.');
    this.name = 'AdminReadTimeoutError';
  }
}

export const ADMIN_READ_TIMEOUT_MS = 15_000;

function readAdminCsrfCookie(): string | null {
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index === -1) continue;
    if (part.slice(0, index).trim() === ADMIN_CSRF_COOKIE_NAME) return part.slice(index + 1).trim();
  }
  return null;
}

async function adminRequest<T>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown; base?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') {
    const csrf = readAdminCsrfCookie();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const read = async (signal?: AbortSignal): Promise<T> => {
    const init: RequestInit = { method, headers, credentials: 'same-origin', ...(signal ? { signal } : {}) };
    if (body !== undefined) init.body = body;

    const response = await fetch(`${options.base ?? ADMIN_API_BASE}${path}`, init);
    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    const payload = isJson ? await response.json().catch(() => null) : null;

    if (!response.ok) throw new AdminApiError(response.status, payload as ApiErrorBody | null);
    return (payload as { data: T }).data;
  };

  // Only reads get an automatic deadline. A timed-out write may already have
  // committed on the server; retrying it could create a second admin action.
  if (method !== 'GET') return read();

  if (options.signal?.aborted) throw new DOMException('The admin read was canceled.', 'AbortError');
  const controller = new AbortController();
  let rejectInterrupted!: (reason: Error) => void;
  const interrupted = new Promise<never>((_, reject) => {
    rejectInterrupted = reject;
  });
  const stop = (reason: Error) => {
    if (controller.signal.aborted) return;
    controller.abort(reason);
    rejectInterrupted(reason);
  };
  const onCancel = () => stop(new DOMException('The admin read was canceled.', 'AbortError'));
  options.signal?.addEventListener('abort', onCancel, { once: true });
  const timeout = setTimeout(() => stop(new AdminReadTimeoutError()), ADMIN_READ_TIMEOUT_MS);
  try {
    // The race also bounds a response body that stalls after its headers, and
    // test fetch implementations that do not honor AbortSignal themselves.
    return await Promise.race([read(controller.signal), interrupted]);
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', onCancel);
  }
}

export const adminGet = <T>(path: string, signal?: AbortSignal) => adminRequest<T>(path, { signal });
export const adminPost = <T>(path: string, body?: unknown) => adminRequest<T>(path, { method: 'POST', body });
export const adminPatch = <T>(path: string, body?: unknown) => adminRequest<T>(path, { method: 'PATCH', body });
export const adminDelete = <T>(path: string) => adminRequest<T>(path, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface AdminMe {
  email: string;
}

export interface AdminListEntry {
  id: string;
  email: string;
  isActive: boolean;
  hasSignedIn: boolean;
  createdBy: string | null;
  createdAt: string;
}

/** Builds a query string, omitting empty values. */
function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export interface AdminMetricsOverview {
  mrrCents: number;
  arrCents: number;
  activeCount: number;
  trialingCount: number;
  pastDueCount: number;
  canceledCount: number;
  newLast30d: number;
  paidThisMonthCents: number;
  paidLastMonthCents: number;
  currencyCode: string;
}

export interface AdminMetricsSnapshot {
  date: string;
  mrrCents: number;
  activeCount: number;
  trialingCount: number;
  pastDueCount: number;
  canceledCount: number;
  newCompanies: number;
}

export interface AdminFailedCharge {
  companyId: string;
  companyName: string | null;
  ownerEmail: string | null;
  invoiceId: string;
  amountDueCents: number;
  currency: string | null;
  occurredAt: string;
  hostedInvoiceUrl: string | null;
}

export interface AdminTopCustomers {
  byMrr: Array<{
    companyId: string;
    companyName: string;
    ownerEmail: string | null;
    plan: string | null;
    billingInterval: string;
    mrrCents: number;
  }>;
  byLifetimePaid: Array<{
    companyId: string;
    companyName: string | null;
    ownerEmail: string | null;
    totalPaidCents: number;
    invoiceCount: number;
  }>;
}

export interface AdminDiscount {
  id: string;
  name: string;
  code: string | null;
  percentOff: number | null;
  amountOffCents: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number | null;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  archivedAt: string | null;
  deactivatedAt: string | null;
}

export interface CreateDiscountInput {
  name: string;
  percentOff?: number;
  amountOffCents?: number;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths?: number;
  maxRedemptions?: number;
  /** YYYY-MM-DD; server stores end of that day in America/New_York. */
  expiresOn?: string;
  expiresAt?: string;
  code?: string;
}

export interface AdminCompanyListParams {
  q?: string;
  status?: string;
  plan?: string;
  interval?: string;
  /** A utm_source, or `(none)` for accounts with nothing recorded. */
  source?: string;
  sort?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
}

export const adminApi = {
  me: (signal?: AbortSignal) => adminGet<AdminMe>('/me', signal),
  logout: () => adminPost<{ ok: true }>('/logout'),

  listAdmins: (signal?: AbortSignal) => adminGet<AdminListEntry[]>('/admins', signal),
  addAdmin: (email: string) => adminPost<{ id: string; email: string }>('/admins', { email }),
  removeAdmin: (id: string) => adminDelete<{ ok: true }>(`/admins/${id}`),
  reactivateAdmin: (id: string) => adminPost<{ ok: true }>(`/admins/${id}/reactivate`),

  metricsOverview: (signal?: AbortSignal) => adminGet<AdminMetricsOverview>('/metrics/overview', signal),
  metricsHistory: (days = 90, signal?: AbortSignal) =>
    adminGet<AdminMetricsSnapshot[]>(`/metrics/history?days=${days}`, signal),
  failedCharges: (signal?: AbortSignal) => adminGet<AdminFailedCharge[]>('/metrics/failed-charges', signal),
  topCustomers: (signal?: AbortSignal) => adminGet<AdminTopCustomers>('/metrics/top-customers', signal),
  backfillInvoices: (status: 'paid' | 'uncollectible', startingAfter?: string) =>
    adminPost<{ imported: number; skipped: number; status: string; nextCursor: string | null }>(
      '/metrics/backfill-invoices',
      { status, ...(startingAfter ? { startingAfter } : {}) },
    ),

  listDiscounts: (signal?: AbortSignal) => adminGet<AdminDiscount[]>('/discounts', signal),
  createDiscount: (input: CreateDiscountInput) => adminPost<AdminDiscount>('/discounts', input),
  deactivateDiscount: (id: string) => adminPost<{ ok: true }>(`/discounts/${id}/deactivate`),
  archiveDiscount: (id: string) => adminPost<{ ok: true }>(`/discounts/${id}/archive`),
  grantDiscount: (companyId: string, discountId: string) =>
    adminPost<{ ok: true }>(`/companies/${companyId}/discount`, { discountId }),
  removeDiscount: (companyId: string) => adminDelete<{ ok: true }>(`/companies/${companyId}/discount`),

  listCompanies: (params: AdminCompanyListParams, signal?: AbortSignal) =>
    adminGet<AdminCompanyList>(`/companies${query({ ...params })}`, signal),
  /** A same-origin download. The session cookie rides along; this is not a fetch. */
  signupExportUrl: (params: Omit<AdminCompanyListParams, 'page' | 'pageSize' | 'sort'>) =>
    `${ADMIN_API_BASE}/companies/signup-export${query({ ...params })}`,
  signupSources: (signal?: AbortSignal) =>
    adminGet<{ sources: { source: string; count: number }[]; none: string }>('/companies/signup-sources', signal),
  getCompany: (id: string, signal?: AbortSignal) => adminGet<AdminCompanyDetail>(`/companies/${id}`, signal),
  extendTrial: (id: string, days: number) =>
    adminPost<{ trialEndsAt: string }>(`/companies/${id}/extend-trial`, { days }),
  endTrial: (id: string) => adminPost<{ trialEndsAt: string }>(`/companies/${id}/end-trial`),
  cancelSubscription: (id: string) => adminPost<{ ok: true }>(`/companies/${id}/cancel-subscription`),
  resumeSubscription: (id: string) => adminPost<{ ok: true }>(`/companies/${id}/resume-subscription`),
  changePlan: (id: string, plan: string) => adminPost<{ ok: true }>(`/companies/${id}/change-plan`, { plan }),
  getPaymentMethod: (id: string, signal?: AbortSignal) =>
    adminGet<{ card: { brand: string; last4: string; expMonth: number | null; expYear: number | null } | null }>(
      `/companies/${id}/payment-method`,
      signal,
    ),
  sendCardUpdateLink: (id: string) =>
    adminPost<{ sent: boolean; recipient: string }>(`/companies/${id}/card-update-link`),
  requestTermination: (id: string) => adminPost<{ ok: true }>(`/companies/${id}/request-termination`),
  clearTermination: (id: string) => adminDelete<{ ok: true }>(`/companies/${id}/request-termination`),
  addNote: (id: string, body: string) => adminPost<AdminCompanyNote>(`/companies/${id}/notes`, { body }),

  listCompanyUsers: (id: string, signal?: AbortSignal) =>
    adminGet<AdminCompanyUser[]>(`/companies/${id}/users`, signal),
  /**
   * Signs this browser in as one of the company's users. The response is
   * incidental — what matters is the tenant session cookie it sets, which is
   * why callers navigate hard rather than routing client-side.
   */
  impersonate: (id: string, userId: string) =>
    adminPost<ImpersonationResult>(`/companies/${id}/impersonate`, { userId }),
};

/**
 * The operator's own security surface.
 *
 * Mounted at `/api/admin/security` rather than under `/api/admin/v1`, because
 * the first three endpoints run with NO admin session at all — they are the
 * second leg of a sign-in, holding only the partial-login cookie the Google
 * leg left behind. Everything after them requires the session the verify call
 * mints. Same base override for both halves so the CSRF handling stays
 * identical.
 */
const SECURITY_BASE = '/api/admin/security';

export interface AdminSecurityStatus {
  enabled: boolean;
  required: boolean;
  methods: { id: string; type: string; label: string; createdAt: string; lastUsedAt: string | null }[];
  availableMethods: string[];
  recoveryCodesRemaining: number;
  freshAuth: { fresh: boolean; expiresAt: string | null };
  trustedDeviceDays: number;
}

export interface AdminChallengeOption {
  methodId: string | null;
  type: 'totp' | 'webauthn' | 'recovery';
  label: string;
}

export const adminSecurityApi = {
  // --- the sign-in leg, before any session exists ---
  pending: (signal?: AbortSignal) =>
    adminRequest<{ pending: boolean; options?: AdminChallengeOption[]; trustedDeviceDays?: number }>('/pending', {
      base: SECURITY_BASE,
      signal,
    }),
  challenge: (body: { type: string; methodId?: string | null }) =>
    adminRequest<{ challengeId: string; type: string; publicKey?: ServerPublicKeyRequest }>('/challenge', {
      method: 'POST',
      body,
      base: SECURITY_BASE,
    }),
  verify: (body: { challengeId: string; code?: string; assertion?: unknown; rememberDevice?: boolean }) =>
    adminRequest<{ verified: boolean; recoveryCodesRemaining?: number }>('/verify', {
      method: 'POST',
      body,
      base: SECURITY_BASE,
    }),

  // --- the operator's own screen, behind the session ---
  status: (signal?: AbortSignal) => adminRequest<AdminSecurityStatus>('/status', { base: SECURITY_BASE, signal }),
  enrollTotp: () =>
    adminRequest<{ methodId: string; secret: string; uri: string }>('/totp/enroll', {
      method: 'POST',
      body: {},
      base: SECURITY_BASE,
    }),
  verifyTotp: (methodId: string, code: string) =>
    adminRequest<{ recoveryCodes: string[] | null }>('/totp/verify', {
      method: 'POST',
      body: { methodId, code },
      base: SECURITY_BASE,
    }),
  enrollSms: (phone: string) =>
    adminRequest<{ methodId: string; challengeId: string; maskedPhone: string }>('/sms/enroll', {
      method: 'POST',
      body: { phone },
      base: SECURITY_BASE,
    }),
  verifySms: (challengeId: string, code: string) =>
    adminRequest<{ recoveryCodes: string[] | null }>('/sms/verify', {
      method: 'POST',
      body: { challengeId, code },
      base: SECURITY_BASE,
    }),
  securityKeyOptions: () =>
    adminRequest<{ challengeId: string; publicKey: ServerPublicKeyCreation }>('/security-keys/register/options', {
      method: 'POST',
      body: {},
      base: SECURITY_BASE,
    }),
  registerSecurityKey: (body: {
    challengeId: string;
    label: string;
    clientDataJson: string;
    attestationObject: string;
    transports?: string[];
  }) =>
    adminRequest<{ methodId: string; recoveryCodes: string[] | null }>('/security-keys/register', {
      method: 'POST',
      body,
      base: SECURITY_BASE,
    }),
  removeMethod: (id: string) => adminRequest<void>(`/methods/${id}`, { method: 'DELETE', base: SECURITY_BASE }),
  elevateOptions: (signal?: AbortSignal) =>
    adminRequest<{ options: AdminChallengeOption[] }>('/elevate/options', { base: SECURITY_BASE, signal }),
  elevateChallenge: (body: { type: string; methodId?: string | null }) =>
    adminRequest<{ challengeId: string; type: string; publicKey?: ServerPublicKeyRequest }>('/elevate/challenge', {
      method: 'POST',
      body,
      base: SECURITY_BASE,
    }),
  elevateVerify: (body: { challengeId: string; code?: string; assertion?: unknown }) =>
    adminRequest<{ verified: boolean; fresh: boolean; expiresAt: string | null }>('/elevate/verify', {
      method: 'POST',
      body,
      base: SECURITY_BASE,
    }),
  trustedDevices: (signal?: AbortSignal) =>
    adminRequest<{
      devices: Array<{ id: string; label: string | null; lastUsedAt: string; expiresAt: string; current: boolean }>;
    }>('/trusted-devices', { base: SECURITY_BASE, signal }),
  clearTrustedDevices: () => adminRequest<void>('/trusted-devices', { method: 'DELETE', base: SECURITY_BASE }),
  sessions: (signal?: AbortSignal) =>
    adminRequest<{
      sessions: Array<{ id: string; lastUsedAt: string; expiresAt: string; current: boolean }>;
    }>('/sessions', { base: SECURITY_BASE, signal }),
  revokeOtherSessions: () =>
    adminRequest<{ revoked: boolean }>('/sessions/revoke-others', { method: 'POST', body: {}, base: SECURITY_BASE }),
  regenerateRecoveryCodes: () =>
    adminRequest<{ codes: string[] }>('/recovery-codes/regenerate', {
      method: 'POST',
      body: {},
      base: SECURITY_BASE,
    }),
};

export interface ImpersonationResult {
  csrfToken: string;
  expiresInMinutes: number;
  user: { id: string; email: string; fullName: string };
  redirectTo: string;
}

// ---------------------------------------------------------------------------
// Affiliate Program: partners, attribution, commissions and the monthly
// payout workflow. Mirrors routes/admin-affiliates.ts one for one.
// ---------------------------------------------------------------------------

export interface AdminAffiliateOverview {
  partners: { total: number; active: number; pendingApplications: number; newThisMonth: number };
  customers: { activePaying: number; mrrCents: number };
  money: {
    eligibleRevenueCents: number;
    commissionAccruedCents: number;
    commissionPaidCents: number;
    blendedCommissionPercent: number;
  };
  queues: { openAttributionClaims: number; openRiskFlags: number };
  payoutsConfigured: boolean;
}

export interface AdminPartnerRow {
  id: string;
  displayName: string;
  contactEmail: string;
  slug: string;
  status: string;
  riskState: string;
  negotiatedRatePercent: number | null;
  paypalOnFile: boolean;
  taxDocumentationOnFile: boolean;
  createdAt: string;
  approvedAt: string | null;
}

export interface AdminPartnerCommissionEntry {
  id: string;
  type: string;
  period: string;
  amountCents: number;
  currency: string;
  grossCents: number | null;
  ratePercent: number | null;
  finalized: boolean;
  payoutId: string | null;
  reason: string | null;
  createdAt: string;
}

export interface AdminPartnerDetail {
  partner: {
    id: string;
    displayName: string;
    contactName: string | null;
    contactEmail: string;
    phone: string | null;
    businessName: string | null;
    website: string | null;
    address: {
      line1: string | null;
      line2: string | null;
      city: string | null;
      state: string | null;
      postalCode: string | null;
      country: string | null;
    };
    partnerType: string | null;
    status: string;
    riskState: string;
    slug: string;
    negotiatedRatePercent: number | null;
    paypalOnFile: boolean;
    taxDocumentationOnFile: boolean;
    createdAt: string;
    approvedAt: string | null;
  };
  customers: Array<{ companyId: string; attributedAt: string; source: string | null; campaign: string | null }>;
  /** Counted without the listing's row cap — `customers` holds the newest 200. */
  customersTotal: number;
  /** The ledger's own currency — what every amount on this screen is in. */
  currency: string;
  commissions: AdminPartnerCommissionEntry[];
  payableBalanceCents: number;
  notes: Array<{ id: string; author: string; note: string; isPublic: boolean; createdAt: string }>;
}

export interface AdminAffiliateApplication {
  id: string;
  affiliateId: string;
  status: string;
  promotionPlan: string;
  createdAt: string;
  decidedAt: string | null;
  decisionReason: string | null;
}

export interface AdminAttributionRequest {
  id: string;
  affiliateId: string;
  customerEmail: string;
  businessName: string | null;
  explanation: string;
  matchedCompanyId: string | null;
  status: string;
  decisionReason: string | null;
  createdAt: string;
}

export interface AdminRiskFlag {
  id: string;
  affiliateId: string | null;
  companyId: string | null;
  referralId: string | null;
  flag: string;
  severity: string;
  detail: string | null;
  createdAt: string;
}

export interface AdminCommissionTier {
  id: string;
  minCustomers: number;
  maxCustomers: number | null;
  ratePercent: number;
  effectiveFrom: string;
}

export interface AdminPayout {
  id: string;
  period: string;
  status: string;
  currency: string;
  partnerCount: number;
  grossRevenueCents: number;
  baseCommissionCents: number;
  bonusCents: number;
  reversalCents: number;
  adjustmentCents: number;
  carryoverInCents: number;
  carryoverOutCents: number;
  heldCents: number;
  totalCents: number;
  calculatedAt: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  completedAt: string | null;
  providerBatchId: string | null;
  failureReason: string | null;
}

export interface AdminPayoutItem {
  id: string;
  affiliateId: string;
  amountCents: number;
  currency: string;
  status: string;
  holdReason: string | null;
  paypalOnFile: boolean;
  carriedForwardCents: number;
  providerStatus: string | null;
  failureReason: string | null;
}

export interface AdminPayoutDetail {
  period: string;
  payout: AdminPayout | null;
  minimumPayoutCents?: number;
  payoutsConfigured?: boolean;
  items: AdminPayoutItem[];
}

export interface AdminPayoutExecution {
  status: string;
  batchId: string | null;
  sentCount: number;
  failureReason: string | null;
}

export interface AdminCommissionAdjustmentInput {
  affiliateId: string;
  /** Signed, in cents — negative is a clawback. */
  amountCents: number;
  entryType: 'admin_adjustment' | 'bonus' | 'correction';
  period: string;
  reason: string;
  internalNotes?: string;
}

export const adminAffiliatesApi = {
  overview: (signal?: AbortSignal) => adminGet<AdminAffiliateOverview>('/affiliates/overview', signal),

  listPartners: (params: { status?: string; page?: number; pageSize?: number } = {}, signal?: AbortSignal) =>
    adminGet<{ page: number; pageSize: number; hasMore: boolean; partners: AdminPartnerRow[] }>(
      `/affiliates/partners${query({ ...params })}`,
      signal,
    ),
  getPartner: (id: string, signal?: AbortSignal) => adminGet<AdminPartnerDetail>(`/affiliates/partners/${id}`, signal),
  // The target status rides in the query string — that is how the route reads
  // it — while the required reason is the JSON body.
  setPartnerStatus: (id: string, status: string, reason: string) =>
    adminPost<{ status: string }>(`/affiliates/partners/${id}/status${query({ status })}`, { reason }),
  addPartnerNote: (id: string, note: string) =>
    adminPost<{ added: true }>(`/affiliates/partners/${id}/notes`, { reason: note }),
  setTaxDocumentation: (id: string, received: boolean, reason: string) =>
    adminPost<{ taxDocumentationOnFile: boolean }>(`/affiliates/partners/${id}/tax-documentation`, {
      received,
      reason,
    }),

  listApplications: (params: { status?: string; affiliateId?: string } = {}, signal?: AbortSignal) =>
    adminGet<{ applications: AdminAffiliateApplication[] }>(`/affiliates/applications${query({ ...params })}`, signal),

  listAttributionRequests: (status?: string, signal?: AbortSignal) =>
    adminGet<{ requests: AdminAttributionRequest[] }>(`/affiliates/attribution-requests${query({ status })}`, signal),
  decideAttributionRequest: (
    id: string,
    body: { status: 'approved' | 'rejected'; reason: string; companyId?: string },
  ) => adminPost<{ status: string }>(`/affiliates/attribution-requests/${id}/decide`, body),
  overrideAttribution: (body: { companyId: string; affiliateId: string; reason: string }) =>
    adminPost<{ attributed: boolean; note: string }>('/affiliates/attribution/override', body),
  removeAttribution: (body: { companyId: string; reason: string }) =>
    adminPost<{ removed: boolean }>('/affiliates/attribution/remove', body),

  createAdjustment: (body: AdminCommissionAdjustmentInput) =>
    adminPost<{ written: boolean }>('/affiliates/adjustments', body),
  listTiers: (signal?: AbortSignal) => adminGet<{ tiers: AdminCommissionTier[] }>('/affiliates/tiers', signal),

  listRiskFlags: (signal?: AbortSignal) => adminGet<{ flags: AdminRiskFlag[] }>('/affiliates/risk', signal),
  resolveRiskFlag: (id: string, reason: string) =>
    adminPost<{ resolved: boolean }>(`/affiliates/risk/${id}/resolve`, { reason }),

  getPayout: (period: string, signal?: AbortSignal) =>
    adminGet<AdminPayoutDetail>(`/affiliates/payouts/${period}`, signal),
  calculatePayout: (period: string) => adminPost<{ payout: AdminPayout }>(`/affiliates/payouts/${period}/calculate`),
  approvePayout: (period: string) => adminPost<{ approved: boolean }>(`/affiliates/payouts/${period}/approve`),
  executePayout: (period: string) => adminPost<AdminPayoutExecution>(`/affiliates/payouts/${period}/execute`),
  reconcilePayout: (period: string) =>
    adminPost<{ payout: AdminPayout | null }>(`/affiliates/payouts/${period}/reconcile`),
};

// ---------------------------------------------------------------------------
// Product Feedback (spec §11–§19, §34–§36)
// ---------------------------------------------------------------------------

/** The list envelope every paged admin endpoint answers in (lib/http.ts). */
export interface AdminPaginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export const adminFeedbackApi = {
  list: (
    params: {
      status?: string;
      category?: string;
      companyId?: string;
      q?: string;
      sort?: string;
      page?: number;
      pageSize?: number;
    },
    signal?: AbortSignal,
  ) =>
    adminGet<AdminPaginated<AdminFeedbackRow>>(
      `/feedback${query(params as Record<string, string | number | undefined>)}`,
      signal,
    ),
  detail: (id: string, signal?: AbortSignal) => adminGet<AdminFeedbackDetail>(`/feedback/${id}`, signal),
  analytics: (signal?: AbortSignal) => adminGet<AdminFeedbackAnalytics>('/feedback/analytics', signal),
  settings: (signal?: AbortSignal) => adminGet<AdminFeedbackSettings>('/feedback/settings', signal),
  saveSettings: (body: Partial<Omit<AdminFeedbackSettings, 'previewConfigured' | 'previewUrl'>>) =>
    adminPatch<AdminFeedbackSettings>('/feedback/settings', body),
  approve: (
    id: string,
    body: {
      title?: string;
      description?: string;
      category?: string | null;
      publicResponse?: string | null;
      internalNote?: string | null;
    },
  ) => adminPost<{ status: string }>(`/feedback/${id}/approve`, body),
  reject: (id: string, body: { reason: string; customerMessage?: string | null; internalNote?: string | null }) =>
    adminPost<{ status: string }>(`/feedback/${id}/reject`, body),
  merge: (
    id: string,
    body: { canonicalRequestId: string; customerMessage?: string | null; internalNote?: string | null },
  ) => adminPost<{ status: string; canonicalRequestId: string }>(`/feedback/${id}/merge`, body),
  setStatus: (id: string, body: { status: string; publicNote?: string | null; internalNote?: string | null }) =>
    adminPatch<{ status: string }>(`/feedback/${id}/status`, body),
  setPreview: (
    id: string,
    body: {
      previewPath?: string | null;
      previewInstructions?: string | null;
      previewQuestion?: string | null;
      publicNote?: string | null;
    },
  ) => adminPatch<{ status: string }>(`/feedback/${id}/preview`, body),
  release: (
    id: string,
    body: {
      releaseVersion?: string | null;
      releaseNotes?: string | null;
      deployedAt?: string;
      publicNote?: string | null;
    },
  ) => adminPatch<{ status: string; deployedAt: string }>(`/feedback/${id}/release`, body),
  setLinks: (
    id: string,
    body: { githubRepository?: string | null; githubIssueNumber?: number | null; githubPullRequest?: number | null },
  ) => adminPatch<{ updated: boolean }>(`/feedback/${id}/links`, body),
};

export interface AdminDemoVisitorRow {
  id: string;
  email: string;
  fullName: string;
  companyName: string;
  emailDomain: string;
  language: string;
  entryCount: number;
  /** Already normalized against the live generation by the API — see admin-public-demo.ts. */
  creditsUsed: number;
  creditsGeneration: number;
  /** When they ticked the /try opt-in box, or null. Necessary but not sufficient. */
  marketingOptInAt: string | null;
  /** When they unsubscribed. Non-null means do not email, whatever the opt-in says. */
  marketingOptOutAt: string | null;
  blockedAt: string | null;
  blockedReason: string | null;
  createdAt: string;
  lastEnteredAt: string | null;
  lastRequestedAt: string | null;
  /** First-touch source, or null when the demo was opened with none. */
  attribution: {
    referrer: string | null;
    landingPath: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
  } | null;
}

export interface AdminPublicDemoState {
  available: boolean;
  enabled: boolean;
  nextResetAt: string | null;
  tenants: {
    companyId: string;
    shardId: string;
    generation: number;
    status: string;
    createdAt: string;
    activatedAt: string | null;
    retiredAt: string | null;
    seedError: string | null;
  }[];
}

export interface AdminDemoInviteRow {
  id: string;
  fullName: string;
  email: string;
  companyName: string;
  language: string;
  note: string | null;
  createdBy: string;
  createdAt: string;
  /** Null means the link never expires. */
  expiresAt: string | null;
  redeemedAt: string | null;
  revokedAt: string | null;
  status: 'open' | 'used' | 'revoked' | 'expired';
}

export interface AdminDemoInviteInput {
  fullName: string;
  email: string;
  companyName?: string;
  language?: 'en' | 'es';
  note?: string | null;
  /** Null or absent for a link that never expires. */
  expiresInDays?: number | null;
}

export const adminPublicDemoApi = {
  state: (signal?: AbortSignal) => adminGet<AdminPublicDemoState>('/public-demo', signal),
  visitors: (params: { search?: string; optedIn?: string; limit?: number; offset?: number }, signal?: AbortSignal) =>
    adminGet<{ visitors: AdminDemoVisitorRow[]; total: number }>(
      `/public-demo/visitors${query(params as Record<string, string | number | undefined>)}`,
      signal,
    ),
  rebuild: () => adminPost<{ companyId: string }>('/public-demo/rebuild'),
  setEnabled: (enabled: boolean) => adminPost<{ enabled: boolean }>('/public-demo/enabled', { enabled }),
  block: (email: string, reason: string) =>
    adminPost<{ visitorId: string; blocked: boolean }>('/public-demo/block', { email, reason }),
  invites: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    adminGet<{ invites: AdminDemoInviteRow[]; total: number }>(
      `/public-demo/invites${query(params as Record<string, string | number | undefined>)}`,
      signal,
    ),
  createInvite: (input: AdminDemoInviteInput) =>
    adminPost<{ id: string; url: string; expiresAt: string | null }>('/public-demo/invites', input),
  revokeInvite: (id: string) =>
    adminPost<{ id: string; revoked: boolean }>(`/public-demo/invites/${encodeURIComponent(id)}/revoke`),
};
